import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import appConfig from './config/appConfig';

const GradientBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50"></div>
    <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-cyan-200/30 to-transparent rounded-full blur-3xl"></div>
  </div>
);

const GlassCard = ({ children, className = "" }) => (
  <div className={`bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`}>
    {children}
  </div>
);

// Multi-Select Dropdown Component
const MultiSelectDropdown = ({ label, options, selectedValues, onChange, placeholder = "Select...", isOpen, onToggle, dropdownId }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isOpen) {
          onToggle(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const handleToggle = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const displayText = selectedValues.length === 0
    ? placeholder
    : selectedValues.length === 1
    ? selectedValues[0]
    : `${selectedValues.length} selected`;

  return (
    <div ref={dropdownRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <button
        type="button"
        onClick={() => onToggle(isOpen ? null : dropdownId)}
        className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between shadow-sm hover:border-gray-400 transition-colors"
      >
        <span className={selectedValues.length === 0 ? 'text-gray-400 font-normal' : 'text-gray-700 font-medium'}>
          {displayText}
        </span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop to prevent interaction with other elements */}
          <div className="fixed inset-0 z-[100]" onClick={() => onToggle(null)}></div>

          {/* Dropdown Menu */}
          <div className="absolute z-[150] w-full mt-1 bg-white border-2 border-blue-400 rounded-lg shadow-2xl max-h-60 overflow-hidden">
            <div className="max-h-60 overflow-y-auto">
              {/* Select All Option */}
              <div
                className="px-4 py-3 hover:bg-blue-100 cursor-pointer border-b-2 border-gray-200 flex items-center space-x-3 sticky top-0 bg-white z-10"
                onClick={handleSelectAll}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.length === options.length && options.length > 0}
                  onChange={() => {}}
                  className="rounded border-2 border-gray-400 text-blue-600 focus:ring-blue-500 w-4 h-4 flex-shrink-0"
                />
                <span className="font-semibold text-gray-800">
                  {selectedValues.length === options.length && options.length > 0 ? 'Deselect All' : 'Select All'}
                </span>
              </div>

              {/* Options */}
              {options.map((option) => (
                <div
                  key={option}
                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer flex items-center space-x-3 bg-white border-b border-gray-100 last:border-b-0"
                  onClick={() => handleToggle(option)}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option)}
                    onChange={() => {}}
                    className="rounded border-2 border-gray-400 text-blue-600 focus:ring-blue-500 w-4 h-4 flex-shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{option}</span>
                </div>
              ))}

              {options.length === 0 && (
                <div className="px-4 py-3 text-gray-500 text-sm bg-white text-center">
                  No options available
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Dynamic Badge Component - generates colors based on value
const DynamicBadge = ({ value, type = 'default' }) => {
  // Generate a consistent hash from the string
  const stringToHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  // Color palettes for different badge types
  const colorPalettes = {
    status: [
      'bg-blue-100 text-blue-700 border-blue-300',
      'bg-sky-100 text-sky-700 border-sky-300',
      'bg-cyan-100 text-cyan-700 border-cyan-300',
      'bg-indigo-100 text-indigo-700 border-indigo-300',
      'bg-slate-100 text-slate-700 border-slate-300',
      'bg-blue-50 text-blue-600 border-blue-200',
    ],
    priority: [
      'bg-red-100 text-red-700 border-red-300',
      'bg-orange-100 text-orange-700 border-orange-300',
      'bg-amber-100 text-amber-700 border-amber-300',
      'bg-blue-100 text-blue-700 border-blue-300',
      'bg-slate-100 text-slate-700 border-slate-300',
    ],
    criticality: [
      'bg-blue-100 text-blue-700 border-blue-300',
      'bg-indigo-100 text-indigo-700 border-indigo-300',
      'bg-sky-100 text-sky-700 border-sky-300',
      'bg-cyan-100 text-cyan-700 border-cyan-300',
      'bg-slate-100 text-slate-700 border-slate-300',
    ],
    approval: [
      'bg-amber-100 text-amber-800 border-amber-300',
      'bg-green-100 text-green-800 border-green-300',
      'bg-red-100 text-red-800 border-red-300',
      'bg-slate-100 text-slate-800 border-slate-300',
      'bg-blue-100 text-blue-800 border-blue-300',
    ]
  };

  const palette = colorPalettes[type] || colorPalettes.status;
  const hash = stringToHash(value);
  const colorClass = palette[hash % palette.length];

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
      {value}
    </span>
  );
};

// Statistics Card Component
const StatCard = ({ title, value, icon, gradient, subtitle }) => (
  <GlassCard className="p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
          {value}
        </p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg`}>
        {icon}
      </div>
    </div>
  </GlassCard>
);

const RequirementApprovalDashboard = () => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add state to track which dropdown is open
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Filter states
  const [statusFilters, setStatusFilters] = useState([]);
  const [approverFilters, setApproverFilters] = useState([]);
  const [approvalStatusFilters, setApprovalStatusFilters] = useState([]);
  const [priorityFilters, setPriorityFilters] = useState([]);
  const [criticalityFilters, setCriticalityFilters] = useState([]);
  const [approvalTypeFilters, setApprovalTypeFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Available filter options
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableApprovers, setAvailableApprovers] = useState([]);
  const [availableApprovalStatuses, setAvailableApprovalStatuses] = useState([]);
  const [availablePriorities, setAvailablePriorities] = useState([]);
  const [availableCriticalities, setAvailableCriticalities] = useState([]);
  const [availableApprovalTypes, setAvailableApprovalTypes] = useState([]);

  // Statistics - now dynamic
  const [stats, setStats] = useState({
    total: 0,
    approvalStatusCounts: {},
    typeCounts: {}
  });

  // Get authentication values
  const corporate_account = localStorage.getItem('corporate_account');
  const current_project = localStorage.getItem('current_project');
  const login_token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!current_project || current_project === 'null') {
      setError('Please select a project from the Requirements Portal');
      return;
    }
    fetchApprovalData();
  }, [current_project]);

  const fetchApprovalData = async (customFilters = null) => {
    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        corporate_account,
        project_id: current_project,
        filter_by_status: customFilters?.status || statusFilters,
        filter_by_approvers: customFilters?.approvers || approverFilters,
        filter_by_approval_status: customFilters?.approvalStatus || approvalStatusFilters,
        requirement_priority: customFilters?.priority || priorityFilters,
        requirement_criticality: customFilters?.criticality || criticalityFilters,
        search_query: customFilters?.search !== undefined ? customFilters.search : searchQuery,
        sort_criteria: "",
        created_date_start: "",
        created_date_end: "",
        updated_date_start: "",
        updated_date_end: "",
        approval_date_start: "",
        approval_date_end: ""
      };

      const response = await fetch(appConfig.getUrl('/get_requirements_by_approver'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        const reqList = data.requirement_list || [];

        // Show ALL approval types - don't filter out FUNCTIONAL LEVEL or PROJECT LEVEL
        let allApprovals = reqList;

        // Apply type filter if selected
        const typeFiltersToUse = customFilters?.approvalType || approvalTypeFilters;
        if (typeFiltersToUse && typeFiltersToUse.length > 0) {
          allApprovals = allApprovals.filter(req => typeFiltersToUse.includes(req.req_type));
        }

        setRequirements(allApprovals);

        // Calculate statistics dynamically
        const approvalStatusCounts = {};
        const typeCounts = {};

        allApprovals.forEach(req => {
          // Count by approval status
          const status = req.approval_status;
          if (status) {
            approvalStatusCounts[status] = (approvalStatusCounts[status] || 0) + 1;
          }

          // Count by type
          const type = req.req_type;
          if (type) {
            typeCounts[type] = (typeCounts[type] || 0) + 1;
          }
        });

        setStats({
          total: allApprovals.length,
          approvalStatusCounts: approvalStatusCounts,
          typeCounts: typeCounts
        });

        // Extract unique values for filters - handle NA values
        const statuses = [...new Set(allApprovals.map(r => r.req_status).filter(s => s && s !== 'NA'))];

        // For approvers, create a map of ID to Name
        const approverMap = new Map();
        allApprovals.forEach(r => {
          if (r.approver_user_id && r.approver_user_name) {
            approverMap.set(r.approver_user_id, r.approver_user_name);
          }
        });
        const approvers = Array.from(approverMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));

        const approvalStatuses = [...new Set(allApprovals.map(r => r.approval_status).filter(Boolean))];
        const priorities = [...new Set(allApprovals.map(r => r.req_priority).filter(p => p && p !== 'NA'))];
        const criticalities = [...new Set(allApprovals.map(r => r.req_criticality).filter(c => c && c !== 'NA'))];
        const types = [...new Set(allApprovals.map(r => r.req_type).filter(Boolean))];

        setAvailableStatuses(statuses.sort());
        setAvailableApprovers(approvers);
        setAvailableApprovalStatuses(approvalStatuses.sort());
        setAvailablePriorities(priorities.sort());
        setAvailableCriticalities(criticalities.sort());
        setAvailableApprovalTypes(types.sort());
      } else {
        setError(data.status_description);
      }
    } catch (err) {
      setError('Failed to fetch approval data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchApprovalData();
  };

  const handleClearFilters = () => {
    setStatusFilters([]);
    setApproverFilters([]);
    setApprovalStatusFilters([]);
    setPriorityFilters([]);
    setCriticalityFilters([]);
    setApprovalTypeFilters([]);
    setSearchQuery('');

    fetchApprovalData({
      status: [],
      approvers: [],
      approvalStatus: [],
      priority: [],
      criticality: [],
      approvalType: [],
      search: ''
    });
  };

  const handleRefresh = () => {
    fetchApprovalData();
  };

  const handleDropdownToggle = (dropdownId) => {
    setOpenDropdownId(dropdownId);
  };

  const hasActiveFilters = statusFilters.length > 0 || approverFilters.length > 0 ||
                          approvalStatusFilters.length > 0 || priorityFilters.length > 0 ||
                          criticalityFilters.length > 0 || approvalTypeFilters.length > 0 ||
                          searchQuery.length > 0;

  // Generate dynamic stat cards based on actual approval statuses
  const generateStatCards = () => {
    const cards = [
      {
        title: "Total Approvals Requested",
        value: stats.total,
        icon: (
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        gradient: "from-blue-500 to-cyan-500"
      }
    ];

    // Add cards for each approval type
    const typeGradients = {
      'REQUIREMENT LEVEL': 'from-blue-500 to-indigo-500',
      'FUNCTIONAL LEVEL': 'from-cyan-500 to-sky-500',
      'PROJECT LEVEL': 'from-indigo-500 to-blue-600'
    };

    Object.entries(stats.typeCounts).forEach(([type, count]) => {
      cards.push({
        title: type,
        value: count,
        icon: (
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        ),
        gradient: typeGradients[type] || 'from-gray-500 to-gray-600',
        subtitle: `${stats.total > 0 ? Math.round((count / stats.total) * 100) : 0}% of total`
      });
    });

    return cards;
  };

  // Helper function to get approval type badge color
  const getApprovalTypeBadge = (type) => {
    const colors = {
      'REQUIREMENT LEVEL': 'bg-blue-100 text-blue-700 border-blue-300',
      'FUNCTIONAL LEVEL': 'bg-cyan-100 text-cyan-700 border-cyan-300',
      'PROJECT LEVEL': 'bg-indigo-100 text-indigo-700 border-indigo-300'
    };
    return colors[type] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  if (!current_project || current_project === 'null') {
    return (
      <div className="min-h-screen">
        <GradientBackground />
        <Header />
        <div className="max-w-7xl mx-auto mt-24 px-4">
          <GlassCard className="p-8 text-center">
            <div className="text-amber-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Project Selected</h2>
            <p className="text-gray-600 mb-6">Please select a project from the Requirements Portal to view approvals.</p>
            <button
              onClick={() => navigate('/RequirementsPortal')}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium"
            >
              Go to Requirements Portal
            </button>
          </GlassCard>
        </div>
      </div>
    );
  }

  const statCards = generateStatCards();

  return (
    <div className="min-h-screen">
      <GradientBackground />
      <Header />

      <div className="max-w-7xl mx-auto mt-24 px-4 pb-8">
        {/* Header Section */}
        <div className="mb-8">
          <GlassCard className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">Requirement Approvals</h1>
                  <p className="text-sm text-gray-600">Review and manage all levels of approvals</p>
                </div>
              </div>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Refreshing...</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Refresh</span>
                  </span>
                )}
              </button>
            </div>
          </GlassCard>
        </div>

        {/* Statistics Cards - Dynamic */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, index) => (
            <StatCard
              key={index}
              title={card.title}
              value={card.value}
              icon={card.icon}
              gradient={card.gradient}
              subtitle={card.subtitle}
            />
          ))}
        </div>

        {/* Approver Statistics Table */}
        {!loading && requirements.length > 0 && (
          <div className="mb-8">
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Approval Statistics by Approver
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-400 to-blue-500">
                      <th className="border-2 border-white px-6 py-3 text-left text-white font-bold text-base">User Name</th>
                      <th className="border-2 border-white px-6 py-3 text-center text-white font-bold text-base">Approvals Requested</th>
                      {(() => {
                        // Get all unique approval statuses
                        const uniqueStatuses = [...new Set(requirements.map(r => r.approval_status).filter(Boolean))].sort();
                        return uniqueStatuses.map(status => (
                          <th key={status} className="border-2 border-white px-6 py-3 text-center text-white font-bold text-base">
                            {status}
                          </th>
                        ));
                      })()}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Get all unique approval statuses
                      const uniqueStatuses = [...new Set(requirements.map(r => r.approval_status).filter(Boolean))].sort();

                      // Calculate statistics by approver
                      const approverStats = {};

                      requirements.forEach(req => {
                        const approverName = req.approver_user_name;
                        const approvalStatus = req.approval_status;

                        if (!approverStats[approverName]) {
                          approverStats[approverName] = {
                            requested: 0,
                            statusCounts: {}
                          };
                          // Initialize all statuses to 0
                          uniqueStatuses.forEach(status => {
                            approverStats[approverName].statusCounts[status] = 0;
                          });
                        }

                        approverStats[approverName].requested += 1;

                        if (approvalStatus) {
                          approverStats[approverName].statusCounts[approvalStatus] =
                            (approverStats[approverName].statusCounts[approvalStatus] || 0) + 1;
                        }
                      });

                      // Convert to array and sort by name
                      const approverArray = Object.entries(approverStats)
                        .map(([name, stats]) => ({ name, ...stats }))
                        .sort((a, b) => a.name.localeCompare(b.name));

                      // Calculate totals
                      const totals = {
                        requested: 0,
                        statusCounts: {}
                      };

                      uniqueStatuses.forEach(status => {
                        totals.statusCounts[status] = 0;
                      });

                      approverArray.forEach(approver => {
                        totals.requested += approver.requested;
                        uniqueStatuses.forEach(status => {
                          totals.statusCounts[status] += approver.statusCounts[status] || 0;
                        });
                      });

                      return (
                        <>
                          {approverArray.map((approver, idx) => (
                            <tr key={approver.name} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/50'} hover:bg-blue-100/50 transition-colors`}>
                              <td className="border-2 border-blue-100 px-6 py-3 font-semibold text-gray-800 text-base">
                                {approver.name}
                              </td>
                              <td className="border-2 border-blue-100 px-6 py-3 text-center text-gray-800 text-lg font-semibold">
                                {approver.requested}
                              </td>
                              {uniqueStatuses.map(status => (
                                <td key={status} className="border-2 border-blue-100 px-6 py-3 text-center text-gray-800 text-lg font-semibold">
                                  {approver.statusCounts[status] || 0}
                                </td>
                              ))}
                            </tr>
                          ))}
                          {/* Totals Row */}
                          <tr className="bg-gradient-to-r from-gray-700 to-gray-800">
                            <td className="border-2 border-white px-6 py-3 font-bold text-white text-base">
                              Totals
                            </td>
                            <td className="border-2 border-white px-6 py-3 text-center text-white text-lg font-bold">
                              {totals.requested}
                            </td>
                            {uniqueStatuses.map(status => (
                              <td key={status} className="border-2 border-white px-6 py-3 text-center text-white text-lg font-bold">
                                {totals.statusCounts[status]}
                              </td>
                            ))}
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </div>
        )}

        {/* Filters Section */}
        <div className="mb-16 relative z-50">
          <GlassCard className="p-6 relative overflow-visible">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters & Search
              </h2>
              {hasActiveFilters && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  Filters Active
                </span>
              )}
            </div>

            {/* Search Bar */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Approvals
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by requirement ID, description, level, or approver..."
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              />
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6 relative z-50">
              <MultiSelectDropdown
                label="Approval Type"
                options={availableApprovalTypes}
                selectedValues={approvalTypeFilters}
                onChange={setApprovalTypeFilters}
                placeholder="All Types"
                isOpen={openDropdownId === 'approvalType'}
                onToggle={handleDropdownToggle}
                dropdownId="approvalType"
              />

              <MultiSelectDropdown
                label="Approval Status"
                options={availableApprovalStatuses}
                selectedValues={approvalStatusFilters}
                onChange={setApprovalStatusFilters}
                placeholder="All Approval Statuses"
                isOpen={openDropdownId === 'approvalStatus'}
                onToggle={handleDropdownToggle}
                dropdownId="approvalStatus"
              />

              <MultiSelectDropdown
                label="Requirement Status"
                options={availableStatuses}
                selectedValues={statusFilters}
                onChange={setStatusFilters}
                placeholder="All Statuses"
                isOpen={openDropdownId === 'status'}
                onToggle={handleDropdownToggle}
                dropdownId="status"
              />

              <MultiSelectDropdown
                label="Approver"
                options={availableApprovers.map(([id, name]) => name)}
                selectedValues={approverFilters.map(id => {
                  const found = availableApprovers.find(([appId]) => appId === id);
                  return found ? found[1] : id;
                })}
                onChange={(selectedNames) => {
                  // Convert names back to IDs
                  const selectedIds = selectedNames.map(name => {
                    const found = availableApprovers.find(([id, n]) => n === name);
                    return found ? found[0] : name;
                  });
                  setApproverFilters(selectedIds);
                }}
                placeholder="All Approvers"
                isOpen={openDropdownId === 'approver'}
                onToggle={handleDropdownToggle}
                dropdownId="approver"
              />

              <MultiSelectDropdown
                label="Priority"
                options={availablePriorities}
                selectedValues={priorityFilters}
                onChange={setPriorityFilters}
                placeholder="All Priorities"
                isOpen={openDropdownId === 'priority'}
                onToggle={handleDropdownToggle}
                dropdownId="priority"
              />

              <MultiSelectDropdown
                label="Criticality"
                options={availableCriticalities}
                selectedValues={criticalityFilters}
                onChange={setCriticalityFilters}
                placeholder="All Criticalities"
                isOpen={openDropdownId === 'criticality'}
                onToggle={handleDropdownToggle}
                dropdownId="criticality"
              />
            </div>

            {/* Filter Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Apply Filters</span>
                </span>
              </button>

              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-lg hover:from-gray-500 hover:to-gray-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Clear All Filters</span>
                  </span>
                </button>
              )}
            </div>
          </GlassCard>
        </div>

        {error && (
          <div className="mb-8">
            <GlassCard className="p-4 bg-red-50 border-red-200">
              <div className="flex items-center space-x-2 text-red-700">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            </GlassCard>
          </div>
        )}

        {loading ? (
          <GlassCard className="p-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <svg className="animate-spin h-12 w-12 text-blue-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-600">Loading approval data...</p>
            </div>
          </GlassCard>
        ) : (
          <GlassCard className="p-6 relative z-0">
            <h2 className="text-xl font-bold text-gray-800 mb-4">All Approvals (Requirements, Functional & Project Level)</h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-cyan-600">
                    <th className="border-2 border-white px-4 py-3 text-left text-white font-semibold text-sm">Type</th>
                    <th className="border-2 border-white px-4 py-3 text-left text-white font-semibold text-sm">Req ID</th>
                    <th className="border-2 border-white px-4 py-3 text-left text-white font-semibold text-sm">Level / Description</th>
                    <th className="border-2 border-white px-4 py-3 text-center text-white font-semibold text-sm">Status</th>
                    <th className="border-2 border-white px-4 py-3 text-center text-white font-semibold text-sm">Priority</th>
                    <th className="border-2 border-white px-4 py-3 text-center text-white font-semibold text-sm">Criticality</th>
                    <th className="border-2 border-white px-4 py-3 text-center text-white font-semibold text-sm">Approver</th>
                    <th className="border-2 border-white px-4 py-3 text-center text-white font-semibold text-sm">Approval Status</th>
                    <th className="border-2 border-white px-4 py-3 text-center text-white font-semibold text-sm">Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {requirements.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="border-2 border-blue-100 px-4 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center space-y-2">
                          <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                          <p className="text-lg font-medium">No approvals found</p>
                          <p className="text-sm">Try adjusting your filters or search criteria</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    requirements.map((req, idx) => (
                      <tr key={req.REQUIREMENT_APPROVER_RECORD_ID} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'} hover:bg-blue-100/50 transition-colors`}>
                        <td className="border-2 border-blue-100 px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getApprovalTypeBadge(req.req_type)}`}>
                            {req.req_type}
                          </span>
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 font-semibold text-blue-700 text-sm">
                          {req.req_id_with_prefix !== 'NA' ? req.req_id_with_prefix : '-'}
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 text-gray-700 text-sm max-w-md">
                          <div className="line-clamp-2" title={req.req_description !== 'NA' ? req.req_description : req.level_description}>
                            {req.req_type === 'REQUIREMENT' && req.req_description !== 'NA'
                              ? req.req_description
                              : req.level_description !== 'NA'
                                ? req.level_description
                                : 'Project Level Approval'}
                          </div>
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 text-center">
                          {req.req_status !== 'NA' ? <DynamicBadge value={req.req_status} type="status" /> : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 text-center">
                          {req.req_priority !== 'NA' ? <DynamicBadge value={req.req_priority} type="priority" /> : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 text-center">
                          {req.req_criticality !== 'NA' ? <DynamicBadge value={req.req_criticality} type="criticality" /> : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 text-sm text-gray-700">
                          <div className="flex flex-col">
                            <span className="font-medium">{req.approver_user_name}</span>
                            <span className="text-xs text-gray-500">{req.approver_user_id}</span>
                          </div>
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 text-center">
                          <DynamicBadge value={req.approval_status} type="approval" />
                        </td>
                        <td className="border-2 border-blue-100 px-4 py-3 text-center text-sm text-gray-600">
                          {req.req_created_date && req.req_created_date !== 'NA'
                            ? new Date(req.req_created_date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
};

export default RequirementApprovalDashboard;